import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type GroupEmoji, type GroupFace, type GroupPage as GroupData, type GroupPost, type GroupReaction } from '../../api';
import { BottomSheet } from '../../components/BottomSheet';
import { Link } from '../../components/Link';
import { PageBack } from '../../components/PageBack';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { plural } from '../../format';
import { navigate } from '../../router';
import {
  CHECK, Clamp, DeleteSheet, FaceStack, GroupFaceView, GroupIcon, GroupPollView, HEART, LOCK, MORE, PostWho, QUESTION, ReactButton, ReactionSummary, ReportSheet, SPEECH,
  groupTint, pollHint, pollMeta, useGroupsLive,
} from './group-parts';
import { withMine } from './group-reactions';

/** A chain's lines its card shows. */
const PEEK = 3;
const EMBER: GroupFace = { name: 'Ember', avatar: null, ember: true };

type Filter = 'all' | 'questions' | 'stories' | 'unanswered';
const FILTERS: Array<[Filter, string]> = [['all', 'All'], ['questions', 'Questions'], ['stories', 'Stories'], ['unanswered', 'Needs an answer']];
const shows = (f: Filter, p: GroupPost): boolean => (
  f === 'all' ? true
    : f === 'questions' ? p.kind === 'question'
      : f === 'stories' ? p.kind === 'share'
        : p.kind === 'question' && p.answer_count === 0
);
const NONE: Record<Filter, string> = {
  all: 'Nothing here yet. Be the first to ask or share.',
  questions: 'No questions yet.',
  stories: 'No stories yet.',
  unanswered: 'Every question here has an answer.',
};

/**
 * A group — `/groups/:groupId` (2026-11-03 founder; redesign 2026-11-04, mock GroupHome, after Facebook Groups). The
 * group's cover in its tint, its icon and name, private · members · posts this week, what it is about, a few faces and
 * Joined (which offers leaving, as ··· does). Not joined yet: how it works and Join. Joined: Posts — a box to ask or
 * share, filters (all, questions, stories, questions with no answer yet), and every post as a card (who did what and
 * when, the question, the start of the background, its emoji and answers, React | Answer, the first answer) — or How it
 * works.
 */
export function GroupPage({ groupId }: { groupId: string }): ReactElement {
  const path = `/groups/${encodeURIComponent(groupId)}`;
  const [page, setPage] = useState<GroupData | null>(null);
  const [failed, setFailed] = useState<'' | 'not_open' | 'load_failed'>('');
  const [sheet, setSheet] = useState<'' | 'new' | 'leave'>('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'posts' | 'how'>('posts');
  const [filter, setFilter] = useState<Filter>('all');
  // A post's ···, from its card: theirs to delete, anyone else's to report.
  const [menu, setMenu] = useState<{ post: GroupPost; act: 'delete' | 'report' } | null>(null);

  const reads = useRef(0);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.group(groupId)
      .then((r) => { if (seq === reads.current) { setPage(r); setFailed(''); } })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true }); return; }
        if (apiErrorCode(err) === 'not_open') { setFailed('not_open'); return; }
        trackFailed('group_page', apiFailureReason(err), { match_id: groupId });
        setFailed('load_failed');
      });
  };
  useEffect(load, [groupId]);
  useGroupsLive(load);

  const join = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.joinGroup(groupId);
      load();
    } catch (err) {
      if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true }); return; }
      trackFailed('group_join', apiFailureReason(err), { match_id: groupId });
      if (apiErrorCode(err) === 'not_open') setFailed('not_open');
      else toast.error('Couldn’t join just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  // An emoji on a post, from its card: drawn at once, the server's answer for the post then stands.
  const setPostReactions = (postId: string, list: GroupReaction[]): void => {
    setPage((cur) => (cur ? { ...cur, posts: cur.posts.map((p) => (p.id === postId ? { ...p, reactions: list } : p)) } : cur));
  };
  const react = async (post: GroupPost, emoji: string | null): Promise<void> => {
    setPostReactions(post.id, withMine(post.reactions, emoji));
    try {
      const out = await emberApi.reactInGroup(post.id, emoji);
      setPostReactions(post.id, out.reactions);
    } catch (err) {
      trackFailed('group_react', apiFailureReason(err), { match_id: groupId, state: 'feed' });
      setPostReactions(post.id, post.reactions);
      toast.error('Couldn’t add that. Try again?');
    }
  };

  // A vote in a poll, from its card: the counts come back with it.
  const vote = async (post: GroupPost, choice: number): Promise<void> => {
    try {
      const out = await emberApi.voteInGroup(post.id, choice);
      setPage((cur) => (cur ? { ...cur, posts: cur.posts.map((p): GroupPost => (p.id === post.id ? { ...p, poll: out.poll } : p)) } : cur));
    } catch (err) {
      trackFailed('group_vote', apiFailureReason(err), { match_id: groupId, state: 'feed' });
      toast.error('Couldn’t save your vote. Try again?');
    }
  };

  const leave = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.leaveGroup(groupId);
      setSheet('');
      navigate('/groups', { replace: true });
    } catch (err) {
      trackFailed('group_leave', apiFailureReason(err), { match_id: groupId });
      toast.error('Couldn’t leave just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  // A post off the feed: reported (they won't see it again) or deleted (gone for everyone).
  const dropPost = (postId: string): void => {
    setPage((cur) => (cur ? { ...cur, posts: cur.posts.filter((p) => p.id !== postId) } : cur));
  };
  const act = async ({ post, act: what }: { post: GroupPost; act: 'delete' | 'report' }): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (what === 'report') await emberApi.reportGroupPost(post.id);
      else await emberApi.deleteGroupPost(post.id);
      setMenu(null);
      toast.success(what === 'report' ? 'Thanks for telling us. You won’t see it again.' : 'Deleted.');
      dropPost(post.id);
    } catch (err) {
      trackFailed(what === 'report' ? 'group_report' : 'group_delete', apiFailureReason(err), { match_id: groupId, state: 'feed' });
      // Gone already: off the feed too.
      if (apiErrorCode(err) === 'post_not_found') { setMenu(null); dropPost(post.id); return; }
      toast.error(what === 'report' ? 'Couldn’t send that just now. Try again?' : 'Couldn’t delete that just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (failed === 'not_open') return <PageFailure title="This group isn’t open to you" detail="Groups open up based on what you’ve talked about with Ember." logState="not_open" />;
  if (!page && failed) return <PageFailure title="Couldn’t open this group" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const { group, posts } = page;
  const [ground, ink] = groupTint(groupId);
  const facts = [
    'Private group',
    group.people > 0 ? plural(group.people, 'member') : 'Be the first one here',
    group.week > 0 ? `${plural(group.week, 'post')} this week` : '',
  ].filter(Boolean).join(' · ');
  const shown = posts.filter((p) => shows(filter, p));

  return (
    <main className="nf-page gr-page gr-group" data-log-seen="group_page" data-log-state={group.joined ? 'joined' : 'not_joined'} data-log-match-id={groupId}>
      <section className="gr-head">
        <div className="gr-cover" style={{ background: ground }}>
          <svg className="gr-cover-art" viewBox="0 0 390 128" preserveAspectRatio="xMaxYMid slice" aria-hidden>
            <circle cx="330" cy="10" r="70" fill={ink} fillOpacity="0.08" />
            <circle cx="40" cy="128" r="56" fill={ink} fillOpacity="0.05" />
          </svg>
          <PageBack to="/groups" className="gr-cover-btn" />
          {group.joined && (
            <button type="button" className="gr-cover-btn" aria-label="Leave group" data-log="group_leave" data-log-state="ask" data-log-match-id={groupId} onClick={() => setSheet('leave')}>
              {MORE}
            </button>
          )}
        </div>
        <div className="gr-id">
          <GroupIcon groupId={groupId} size={64} className="gr-id-icon" />
          <h1 className="gr-name" dir="auto">{group.name}</h1>
          <p className="gr-facts"><span className="gr-facts-lock">{LOCK}</span>{facts}</p>
          {group.about && <p className="gr-about" dir="auto">{group.about}</p>}
          {group.joined ? (
            <div className="gr-members">
              <span className="gr-members-who">
                <FaceStack faces={[...(group.faces ?? []).slice(0, 3), EMBER]} size={30} />
                <span className="gr-members-names" dir="auto">{membersLine(group.faces ?? [], group.people)}</span>
              </span>
              <button type="button" className="gr-joined" aria-haspopup="dialog" data-log="group_leave" data-log-state="ask_joined" data-log-match-id={groupId} onClick={() => setSheet('leave')}>
                {CHECK}Joined
              </button>
            </div>
          ) : (
            <>
              <button type="button" className="nf-cta is-block gr-join-big" disabled={busy} data-log="group_join" data-log-state="page" data-log-match-id={groupId} onClick={() => { void join(); }}>
                Join group
              </button>
              <p className="gr-as">You’ll show up as <strong dir="auto">{group.me.name}</strong>, never your real name.</p>
            </>
          )}
        </div>
        {group.joined && (
          <div className="gr-tabs" role="tablist" aria-label="Group">
            {([['posts', 'Posts'], ['how', 'How it works']] as const).map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} className={`gr-tab${tab === key ? ' is-on' : ''}`}
                data-log="group_tab" data-log-state={key} data-log-match-id={groupId} onClick={() => setTab(key)}>
                {label}
              </button>
            ))}
          </div>
        )}
      </section>

      {!group.joined || tab === 'how' ? <HowItWorks me={group.me} /> : (
        <>
          <section className="gr-composer">
            <div className="gr-composer-row">
              <GroupFaceView face={group.me} size={40} />
              <button type="button" className="gr-composer-field" data-log="group_ask_or_share" data-log-match-id={groupId} onClick={() => setSheet('new')}>
                Ask the group, or share what’s going on…
              </button>
            </div>
            <div className="gr-composer-pills">
              <Link to={`${path}/new?kind=question`} className="gr-pill is-ask" data-log="group_choose" data-log-state="question" data-log-match-id={groupId}>{QUESTION}Ask a question</Link>
              <Link to={`${path}/new?kind=share`} className="gr-pill" data-log="group_choose" data-log-state="share" data-log-match-id={groupId}>{HEART}Share what’s going on</Link>
            </div>
          </section>

          <div className="gr-chips" role="group" aria-label="Show">
            {FILTERS.map(([key, label]) => (
              <button key={key} type="button" className={`gr-chip${filter === key ? ' is-on' : ''}`} aria-pressed={filter === key}
                data-log="group_filter" data-log-state={key} data-log-match-id={groupId} onClick={() => setFilter(key)}>
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className="gr-feed">
            {shown.length === 0
              ? <p className="gr-empty">{NONE[filter]}</p>
              : shown.map((p) => (
                <PostCard key={p.id} post={p} emoji={page.emoji}
                  onReact={(e) => { void react(p, e); }} onVote={(c) => vote(p, c)} onMenu={(what) => setMenu({ post: p, act: what })} />
              ))}
          </div>
        </>
      )}

      {sheet === 'new' && (
        <BottomSheet title="What would you like to post?" className="gr-sheet" logSeen="group_chooser" logSeenAttrs={{ 'data-log-match-id': groupId }} onClose={() => setSheet('')}>
          <Link to={`${path}/new?kind=question`} className="gr-choice" data-log="group_choose" data-log-state="question" data-log-match-id={groupId}>
            <span className="gr-choice-icon is-ask">{QUESTION}</span>
            <span className="gr-choice-text">
              <span className="gr-choice-title">Ask a question</span>
              <span className="gr-choice-sub">You’d like answers. Say your question, then a little background.</span>
            </span>
          </Link>
          <Link to={`${path}/new?kind=share`} className="gr-choice" data-log="group_choose" data-log-state="share" data-log-match-id={groupId}>
            <span className="gr-choice-icon">{HEART}</span>
            <span className="gr-choice-text">
              <span className="gr-choice-title">Share what’s going on</span>
              <span className="gr-choice-sub">No answer needed. Just get it off your chest. People can still react or reply.</span>
            </span>
          </Link>
          <p className="gr-choice-as">
            <GroupFaceView face={group.me} size={26} />
            <span>You’ll show up as <strong dir="auto">{group.me.name}</strong>. Only the group sees it.</span>
          </p>
        </BottomSheet>
      )}

      {sheet === 'leave' && (
        <BottomSheet
          title="Leave this group?"
          description="You won’t see it anymore, and you can’t rejoin on your own. What you posted stays."
          logSeen="group_leave_sheet"
          logSeenAttrs={{ 'data-log-match-id': groupId }}
          onClose={() => setSheet('')}
        >
          <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="group_leave" data-log-state="confirm" data-log-match-id={groupId} onClick={() => { void leave(); }}>
            Leave group
          </button>
          <button type="button" className="ember-btn ember-btn-ghost" data-log="group_leave" data-log-state="keep" onClick={() => setSheet('')}>
            Stay
          </button>
        </BottomSheet>
      )}

      {menu?.act === 'report' && (
        <ReportSheet answer={false} busy={busy} groupId={groupId} onReport={() => { void act(menu); }} onClose={() => setMenu(null)} />
      )}
      {menu?.act === 'delete' && (
        <DeleteSheet what="post" under={menu.post.answer_count > 0} postKind={menu.post.kind} busy={busy} groupId={groupId}
          onDelete={() => { void act(menu); }} onClose={() => setMenu(null)} />
      )}
    </main>
  );
}

/** "Sage, Hayden and 15 others": the first two of the others by name, then how many more (`people` counts them too). */
function membersLine(faces: GroupFace[], people: number): string {
  const others = Math.max(people - 1, faces.length);
  if (others === 0) return 'Just you so far';
  const names = faces.slice(0, 2).map((f) => f.name);
  const rest = others - names.length;
  if (rest > 0) return names.length ? `${names.join(', ')} and ${plural(rest, 'other')}` : plural(others, 'other member');
  return names.join(' and ');
}

/** How a group works, in a few lines (its own tab; above Join before they join). */
function HowItWorks({ me }: { me: GroupFace }): ReactElement {
  const lines: Array<[ReactElement, string]> = [
    [<GroupIconSmall key="g" />, 'A private group for people living through the same thing.'],
    [QUESTION, 'Post a question, or share what’s going on, whenever you need.'],
    [CLOCK, 'Others answer when they stop by. There’s no live chat, so there’s no rush.'],
    [MASK, `Everyone here goes by a made-up name. You’re ${me.name}.`],
    [SHIELD, 'Every post and answer gets a quick read first, to keep the group kind and safe.'],
    [FLAME, 'Ember is here too. It answers, and sometimes starts a quick poll or a chain.'],
  ];
  return (
    <section className="gr-how" aria-labelledby="gr-how-title">
      <h2 id="gr-how-title" className="gr-how-title">How it works</h2>
      <ul className="gr-how-list">
        {lines.map(([icon, text]) => (
          <li key={text}><span className="gr-how-icon">{icon}</span><span>{text}</span></li>
        ))}
      </ul>
    </section>
  );
}

function GroupIconSmall(): ReactElement {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="8" cy="8" r="3" />
      <circle cx="17" cy="11" r="2.4" />
      <path d="M3 19c.8-3 2.8-4.6 5-4.6s4.2 1.6 5 4.6M14 19c.6-2.2 1.9-3.4 3.4-3.4" />
    </svg>
  );
}
const CLOCK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const MASK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c1-3.6 3.8-5.4 7-5.4s6 1.8 7 5.4" />
  </svg>
);
const SHIELD = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const FLAME = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path d="M12 3c2 3 5 5.2 5 9.2A5 5 0 0 1 7 12.2C7 8.6 10 6.6 12 3z" fill="currentColor" />
  </svg>
);

/**
 * One post in the feed, as a card (mock GroupHome). A question or a share: who did what and when (and ··· — delete
 * theirs, report anyone else's), the question in bold, the start of the background or what was shared, its emoji and
 * how many answered, React | Answer (Reply on a share or their own question), and the first answer as a bubble with
 * "View all". Ember's poll: its question and its options, to vote right here. Ember's chain: its opening line, the first
 * few lines, how many so far, and Add your line until theirs is there.
 */
function PostCard({ post, emoji, onReact, onVote, onMenu }: {
  post: GroupPost;
  emoji: GroupEmoji | undefined;
  onReact: (emoji: string | null) => void;
  onVote: (choice: number) => Promise<void>;
  onMenu: (act: 'delete' | 'report') => void;
}): ReactElement {
  const to = `/groups/p/${encodeURIComponent(post.id)}`;
  const g = post.group_id;
  const n = post.answer_count;
  const first = post.first_answer;
  const opened = post.kind === 'question' ? (first ? 'answered' : 'waiting') : post.kind;
  const open = (cls: string, children: ReactElement | ReactElement[] | string): ReactElement => (
    <Link to={to} className={cls} data-log="group_post_open" data-log-state={opened} data-log-match-id={g}>{children}</Link>
  );

  if (post.kind === 'poll' && post.poll) {
    return (
      <article className="gr-card">
        <PostWho post={post} size={40} meta={pollMeta(post.poll)} />
        {open('gr-card-open', <span className="gr-card-title" dir="auto">{post.question}</span>)}
        <GroupPollView poll={post.poll} onVote={onVote} groupId={g} where="feed" />
        <div className="gr-card-foot">
          <span>{pollHint(post.poll)}</span>
          {n > 0
            ? open('gr-card-link', plural(n, 'reply', 'replies'))
            : <Link to={`${to}?answer=1`} className="gr-card-link" data-log="group_answer_open" data-log-state="feed" data-log-match-id={g}>Reply</Link>}
        </div>
      </article>
    );
  }

  if (post.kind === 'chain') {
    const lines = post.answers ?? [];
    const added = lines.some((a) => a.author.me);
    return (
      <article className="gr-card">
        <PostWho post={post} size={40} meta="everyone adds one line" />
        {open('gr-card-open', <span className="gr-card-title" dir="auto">{post.question}</span>)}
        {lines.length > 0 && open('gr-peek', (
          <ol className="gr-peek-list">
            {lines.slice(0, PEEK).map((a, i) => (
              <li key={a.id} dir="auto"><strong className="gr-peek-n">{i + 1}</strong><strong>{a.author.me ? 'You' : a.author.name}</strong> {a.text}</li>
            ))}
          </ol>
        ))}
        <div className="gr-card-foot">
          <span>{n > 0 ? `${plural(n, 'line')} so far` : 'Be the first to add a line'}</span>
          {added
            ? open('gr-card-link', 'See all')
            : <Link to={`${to}?answer=1`} className="gr-add-line" data-log="group_line_open" data-log-state="feed" data-log-match-id={g}>Add your line</Link>}
        </div>
      </article>
    );
  }

  const question = post.kind === 'question';
  const word = question && !post.author.me ? 'Answer' : 'Reply';
  const count = question ? (n > 0 ? plural(n, 'answer') : 'No answers yet') : (n > 0 ? plural(n, 'reply', 'replies') : 'No replies yet');
  return (
    <article className="gr-card is-talk">
      <PostWho post={post} size={40} more={post.author.ember ? null : (
        <button type="button" className="gr-card-more" aria-label={post.author.me ? 'Delete this post' : 'Report this post'}
          data-log={post.author.me ? 'group_delete' : 'group_report'} data-log-state={post.author.me ? 'ask_post' : 'ask'} data-log-match-id={g}
          onClick={() => onMenu(post.author.me ? 'delete' : 'report')}>
          {MORE}
        </button>
      )} />
      {open('gr-card-open', question ? [
        <span key="q" className="gr-card-title" dir="auto">{post.question}</span>,
        ...(post.body ? [<Clamp key="b" text={post.body} lines={3} className="gr-card-body" />] : []),
      ] : <Clamp text={post.body} lines={5} className="gr-card-share" />)}
      <div className="gr-counts">
        <ReactionSummary reactions={post.reactions} />
        {n > 0 ? open('gr-counts-link', count) : <span className="gr-counts-none">{count}</span>}
      </div>
      <div className="gr-acts">
        <ReactButton reactions={post.reactions} emoji={emoji} onReact={onReact} groupId={g} what="post" look="bar" />
        <Link to={`${to}?answer=1`} className="gr-act" data-log="group_answer_open" data-log-state="feed" data-log-match-id={g}>{SPEECH}{word}</Link>
      </div>
      {first && (
        <>
          {open('gr-first', [
            <GroupFaceView key="f" face={first.author} size={30} />,
            <span key="b" className={`gr-bubble${first.author.ember ? ' is-ember' : ''}`}>
              <span className="gr-bubble-name" dir="auto">{first.author.name}</span>
              <span className="gr-bubble-text gr-clamp" style={{ WebkitLineClamp: 2 }} dir="auto">{first.text}</span>
            </span>,
          ])}
          {n > 1 && open('gr-view-all', `View all ${n} ${question ? 'answers' : 'replies'}`)}
        </>
      )}
    </article>
  );
}
